import * as types from "./teamSmartImagesTypes";
import { schemas, CALL_API, SERVICES } from "state/middleware/api";
import { getCurrentTeamId } from "state/ui/currentTeam/currentTeamSelectors";
import { makeToast } from "state/ui/toaster/ToasterActions";
import style from "views/components/SmartImageTransferModal/style.module.css";
import { noop } from "lib";
import { addBrandKitImage } from "state/ui/brandKit/BrandKitActions";
import { addContentToTeamFolder } from "state/entities/designFolders/designFoldersActions";
import { currentTeamIdSelector } from "state/entities/teams/teamsSelectors";
import { addBrandKitLogo } from "state/ui/brandKit/BrandKitActions";

const PAGE_SIZE = 100;

export const fetchTeamSmartImages = ({
  params,
  onSuccess = noop,
  page = 1,
  resolve = noop,
  reject = noop
} = {}) => (dispatch, getState) => {
  const state = getState();
  const teamId = getCurrentTeamId(state);
  let currentPage = page;

  dispatch({
    [CALL_API]: {
      types: [
        types.TEAM_SMART_IMAGES_REQUEST,
        types.TEAM_SMART_IMAGES_REQUEST_SUCCESS,
        types.TEAM_SMART_IMAGES_REQUEST_FAILURE
      ],
      service: SERVICES.ASSET,
      endpoint: `/teams/${teamId}/smart-images`,
      schema: schemas.TEAM_SMART_IMAGES_ARRAY,
      request: {
        params,
        page: currentPage,
        pageSize: PAGE_SIZE
      },
      onSuccess: response => {
        if (response && response.ids && response.ids.length >= PAGE_SIZE) {
          dispatch(
            fetchTeamSmartImages({ params, onSuccess, page: currentPage + 1 })
          );
        } else {
          resolve(response);
        }
        onSuccess();
      },
      onFailure: response => reject(response)
    }
  });
};

export const transferTeamSmartImage = ({
  smartImage,
  onSuccess = noop
}) => dispatch => {
  dispatch(
    updateTeamSmartImage({
      smartImage,
      onSuccess: () => {
        dispatch(
          makeToast({
            textComponent: `Your Smart Image ID has been transferred to a new image. Existing designs using the Smart Image will automatically update to use the new image.`,
            type: "noButton",
            icon: "success",
            className: style.smartImageSuccessToast,
            duration: "5000ms"
          })
        );
        onSuccess();
      }
    })
  );
};

/**
 * @desc Updates a team smart image
 * @param {Number} smartImage - Smart Image entity
 */
export const updateTeamSmartImage = ({ smartImage, onSuccess = noop }) => (
  dispatch,
  getState
) => {
  const state = getState();
  const teamId = getCurrentTeamId(state);

  dispatch({
    [CALL_API]: {
      method: "PUT",
      onSuccess,
      types: [
        types.TEAM_SMART_IMAGE_UPDATE_REQUEST,
        types.TEAM_SMART_IMAGE_UPDATE_REQUEST_SUCCESS,
        types.TEAM_SMART_IMAGE_UPDATE_REQUEST_FAILURE
      ],
      service: SERVICES.ASSET,
      endpoint: `/teams/${teamId}/smart-images/${smartImage.id}`,
      schema: schemas.USER_TEAM_SMART_IMAGE,
      request: {
        body: {
          id: smartImage.id,
          teamId,
          label: smartImage.label,
          mediaId: smartImage.mediaId,
          createdAt: smartImage.createdAt,
          updatedAt: smartImage.updatedAt
        }
      }
    }
  });
};

/**
 * @desc Creates a team smart image
 * @param {string} label - the label of the Smart Image entity
 * @param {Number} mediaId - The id of the media entity
 */
export const createTeamSmartImage = ({ label, mediaId, onSuccess = noop }) => (
  dispatch,
  getState
) => {
  const state = getState();
  const teamId = getCurrentTeamId(state);

  dispatch({
    [CALL_API]: {
      method: "POST",
      onSuccess,
      types: [
        types.TEAM_SMART_IMAGE_UPLOAD_REQUEST,
        types.TEAM_SMART_IMAGE_UPLOAD_REQUEST_SUCCESS,
        types.TEAM_SMART_IMAGE_UPLOAD_REQUEST_FAILURE
      ],
      service: SERVICES.ASSET,
      endpoint: `/teams/${teamId}/smart-images`,
      schema: schemas.TEAM_SMART_IMAGE,
      request: {
        body: {
          teamId,
          label,
          mediaId
        }
      }
    }
  });
};

/**
 * @desc Delete a team smart image
 * @param {Number} id - The id of the smart image entity
 * @param {Number} mediaId - The id of the media entity
 */
export const deleteTeamSmartImage = ({ id, mediaId, onSuccess = noop }) => (
  dispatch,
  getState
) => {
  const state = getState();
  const teamId = getCurrentTeamId(state);

  dispatch({
    [CALL_API]: {
      method: "DELETE",
      onSuccess,
      types: [
        types.TEAM_SMART_IMAGE_DELETE_REQUEST,
        types.TEAM_SMART_IMAGE_DELETE_REQUEST_SUCCESS,
        types.TEAM_SMART_IMAGE_DELETE_REQUEST_FAILURE
      ],
      service: SERVICES.ASSET,
      endpoint: `/teams/${teamId}/smart-images/${id}`,
      schema: schemas.NONE,
      extra: {
        mediaId
      }
    }
  });
};

export const handleTeamSmartImageUploadTransfer = ({
  file,
  onSuccess = noop,
  folderId,
  smartImage
}) => (dispatch, getState) => {
  const state = getState();
  const teamId = currentTeamIdSelector(state);

  dispatch(
    addBrandKitImage({
      teamId,
      file,
      placeholderURL: window.URL.createObjectURL(file),
      onSuccess: res => {
        if (folderId) {
          dispatch(
            addContentToTeamFolder({
              contentId: res.mediaId,
              folderId,
              teamId,
              type: "BRAND_IMAGE",
              isFolderUpload: true
            })
          );
        }
        dispatch(
          transferTeamSmartImage({
            smartImage: {
              ...smartImage,
              mediaId: res.mediaId
            },
            onSuccess
          })
        );
      }
    })
  );
};

export const handleTeamSmartLogoUploadTransfer = ({
  file,
  onSuccess = noop,
  folderId,
  smartImage
}) => (dispatch, getState) => {
  const state = getState();
  const teamId = currentTeamIdSelector(state);

  dispatch(
    addBrandKitLogo({
      teamId,
      file,
      onSuccess: res => {
        if (folderId) {
          dispatch(
            addContentToTeamFolder({
              contentId: res.mediaId,
              folderId,
              teamId,
              type: "BRAND_LOGO",
              isFolderUpload: true
            })
          );
        }
        dispatch(
          transferTeamSmartImage({
            smartImage: {
              ...smartImage,
              mediaId: res.mediaId
            },
            onSuccess
          })
        );
      }
    })
  );
};
